import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import ReCAPTCHA from 'react-google-recaptcha';
import { validatePassword } from '../../utils/validation';
import { API_ENDPOINTS } from '../../config/api';
import { authUtils } from '../../utils/auth';

const NewPassword: React.FC = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [passwordErrors, setPasswordErrors] = useState<string[]>([]);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  
  const email = authUtils.getEmail() || searchParams.get('email');
  const resetKey = authUtils.getResetKey() || searchParams.get('reset_key');

  useEffect(() => {
    if (!email || !resetKey) {
      navigate('/forgot-password');
    }
  }, [email, resetKey, navigate]);

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    const validation = validatePassword(value);
    setPasswordErrors(validation.errors);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const passwordValidation = validatePassword(password);
    if (!passwordValidation.isValid) {
      setError('Please fix the password errors below');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    const recaptchaToken = await recaptchaRef.current?.executeAsync();
    if (!recaptchaToken) {
      setError('Please complete the CAPTCHA verification');
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(API_ENDPOINTS.AUTH.CHANGE_PASSWORD, {
        email,
        new_password: password,
        password_reset_key: resetKey,
        recaptchaToken,
      });

      if (response.status === 200) {
        authUtils.removeEmail();
        authUtils.removeResetKey();
        navigate('/login');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to reset password. Please try again.');
      recaptchaRef.current?.reset();
    } finally {
      setLoading(false);
    }
  };

  const isFormValid = () => {
    return (
      password.length > 0 &&
      confirmPassword.length > 0 &&
      password === confirmPassword &&
      validatePassword(password).isValid
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-jobesta-gray-20 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-jobesta shadow-jobesta-card">
        <div>
          <h1 className="text-center text-3xl mb-2 tracking-tight">
            <span className="font-black text-jobesta-blue">Job</span>
            <span className="font-extralight text-jobesta-blue">esta</span>
          </h1>
          <h2 className="text-center text-xl font-semibold text-jobesta-gray-90">
            Set your new password
          </h2>
          <p className="mt-2 text-center text-sm text-jobesta-gray-60">
            Please enter your new password below
          </p>
        </div>
        <form className="mt-8 space-y-6 ph-no-capture" onSubmit={handleSubmit}>
          <div className="space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-jobesta-gray-70 mb-2">
                New Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-jobesta-gray-40 placeholder-jobesta-gray-60 text-jobesta-gray-90 rounded-jobesta focus:outline-none focus:ring-2 focus:ring-jobesta-lightblue focus:ring-opacity-30 focus:border-jobesta-blue focus:z-10 sm:text-sm"
                placeholder="Enter new password"
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
              />
              {passwordErrors.length > 0 && (
                <ul className="mt-2 text-sm text-jobesta-red">
                  {passwordErrors.map((error, index) => (
                    <li key={index}>• {error}</li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-jobesta-gray-70 mb-2">
                Confirm New Password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-jobesta-gray-40 placeholder-jobesta-gray-60 text-jobesta-gray-90 rounded-jobesta focus:outline-none focus:ring-2 focus:ring-jobesta-lightblue focus:ring-opacity-30 focus:border-jobesta-blue focus:z-10 sm:text-sm"
                placeholder="Confirm new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {confirmPassword && password !== confirmPassword && (
                <p className="mt-2 text-sm text-jobesta-red">Passwords do not match</p>
              )}
            </div>
          </div>

          {error && (
            <div className="rounded-jobesta bg-jobesta-red bg-opacity-10 p-4 border border-jobesta-red">
              <div className="flex">
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-jobesta-red">{error}</h3>
                </div>
              </div>
            </div>
          )}

          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ''}
            size="invisible"
          />

          <div>
            <button
              type="submit"
              disabled={loading || !isFormValid()}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-jobesta text-white bg-jobesta-blue hover:bg-jobesta-darkblue focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-jobesta-lightblue transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Resetting password...' : 'Reset Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewPassword;